import {
  Button,
  Checkbox,
  Icons,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';
import { MultiSelect } from '@commenda/kakashi/src/components/MultiSelect/MultiSelect';
import { classNames } from '@commenda/utils';
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { CustomColumnDef } from './extend-table';

export interface PaginationConfigProps {
  isPaginated: boolean;
  pageRowCount: number;
  placeholder: string;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageSizeChange?: (pageSize: number) => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  currentPageSize?: number;
}

export interface FilterConfigProps {
  isFilterEnabled: boolean;
  isSearchEnabled?: boolean;
  filterColumn?: string;
  filterPlaceholderValue?: string;
  searchableColumns: string[];
  searchPlaceholder: string;
}

export interface DataTableProps<TData, TValue> {
  columns: CustomColumnDef<TData, TValue>[];
  data: TData[];
  paginationConfig?: PaginationConfigProps;
  filterConfig?: FilterConfigProps;
  tableContainerClassName?: string;
  rowClassName?: string;
  headerClassName?: string;
  tableHeadClassName?: string;
  tableClassName?: string;
  tableCellClassName?: string;
  isLoading?: boolean;
  selectedRows?: string[];
  handleClick?: (row: TData) => void;
  tableNav?: (
    tableColumns: CustomColumnDef<TData, TValue>[]
  ) => React.ReactNode;
  floatingActionItems?: () => React.ReactNode;
  onExport?: (selectedRows: TData[]) => void;
  onDelete?: (selectedRows: TData[]) => void;
}

enum TableView {
  ThreeRows = 'ThreeRows',
  TwoRows = 'TwoRows',
}

const FloatingActionBar = ({
  renderChildren,
  selectedCount,
  onClose,
}: {
  selectedCount: number;
  onClose: () => void;
  renderChildren: () => React.ReactNode;
}) => {
  if (!renderChildren) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 w-[800px] bg-bg-solid rounded-2xl">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center w-full">{renderChildren()}</div>
      </div>
    </div>
  );
};

export function SalesTaxCustomTable<TData, TValue>({
  columns,
  data,
  paginationConfig = {
    isPaginated: false,
    pageRowCount: 10,
    placeholder: '',
  },
  filterConfig = {
    isFilterEnabled: false,
    filterColumn: '',
    filterPlaceholderValue: '',
    searchableColumns: [],
    searchPlaceholder: '',
  },
  tableContainerClassName = '',
  rowClassName = '',
  headerClassName = '',
  tableHeadClassName = '',
  tableClassName = '',
  tableCellClassName = '',
  isLoading = false,
  handleClick,
  tableNav,
  selectedRows,
  floatingActionItems,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [tableView, setTableView] = useState(TableView.TwoRows);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [searchValue, setSearchValue] = useState('');
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const table = useReactTable({
    // React-table doesn't like when you pass it 'undefined',
    // but we can still access the passed-in data object (which may be undefined) later on.
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    meta: {
      sharedState: {
        key: false,
      },
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),

    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  const tableColumns = table.getAllColumns();

  const columnOptions = tableColumns.map((column) => ({
    label:
      (column.columnDef as CustomColumnDef<TData, unknown>).title || column.id,
    value: column.id,
  }));

  const activeState = 'bg-bg-tertiary  rounded-md ';

  const inactiveState = '';

  const handleSearch = (value: string) => {
    setSearchValue(value);
    filterConfig.searchableColumns.forEach((columnId) => {
      table.getColumn(columnId)?.setFilterValue(value);
    });
  };

  const [filteredValues, setFilteredValues] = useState([]);

  return (
    <div className="relative">
      {tableNav && (
        <div className="flex items-center justify-between rounded-md p-4 bg-bg-secondary">
          {tableNav(tableColumns)}
        </div>
      )}
      <div
        className={classNames(
          'flex flex-col border overflow-hidden',
          tableContainerClassName
        )}
      >
        <div className="m-xl flex items-center justify-between">
          {filterConfig.isSearchEnabled ? (
            <Input
              placeholder={filterConfig.searchPlaceholder}
              leftEl={() => {
                return <Icons.SearchSm className="text-text-quaternary" />;
              }}
              value={searchValue}
              onChange={(event) => handleSearch(event.target.value)}
              className="w-[320px]"
            />
          ) : (
            <div />
          )}
          {filterConfig?.isFilterEnabled && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div
                  onClick={() => setTableView(TableView.TwoRows)}
                  className={classNames(
                    'h-9 w-9 shrink-0 flex items-center justify-center cursor-pointer',
                    tableView === TableView.TwoRows
                      ? activeState
                      : inactiveState
                  )}
                >
                  <Icons.Rows02
                    className={classNames(
                      'h-4 w-4',
                      tableView === TableView.TwoRows ? '' : 'text-fg-disabled'
                    )}
                  />
                </div>
                <div
                  onClick={() => setTableView(TableView.ThreeRows)}
                  className={classNames(
                    'h-9 w-9 shrink-0 flex items-center justify-center cursor-pointer',
                    tableView === TableView.ThreeRows
                      ? activeState
                      : inactiveState
                  )}
                >
                  <Icons.Rows03
                    className={classNames(
                      'h-4 w-4',
                      tableView === TableView.ThreeRows
                        ? ''
                        : 'text-fg-disabled'
                    )}
                  />
                </div>
              </div>
              <MultiSelect
                id="select-columns-multiselect"
                options={columnOptions}
                _className="min-w-[240px]"
                renderOption={({ option, isSelected, onSelect }) => {
                  return (
                    <div
                      onClick={() => {
                        onSelect(option.value);
                        const column = table?.getColumn(option.value);
                        const newVisibility = !column.getIsVisible();
                        column.toggleVisibility(newVisibility);
                      }}
                      className="flex items-center justify-between w-full"
                    >
                      <p>{option.label}</p>
                      <Checkbox
                        className="h-5 w-5"
                        checked={table?.getColumn(option.value)?.getIsVisible()}
                        // checked={isSelected}
                        onChange={() => {
                          onSelect(option.value);
                        }}
                      />
                    </div>
                  );
                }}
                onValueChange={(selectedColumnIds: string[]) => {
                  setFilteredValues(selectedColumnIds);
                }}
                defaultValue={[]}
                hideSelectAll
                placeholder="Customise columns"
              />
            </div>
          )}
        </div>

        <Table className={tableClassName}>
          <TableHeader
            {...(headerClassName ? { className: headerClassName } : {})}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      {...(tableHeadClassName
                        ? { className: tableHeadClassName }
                        : {})}
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={
                    handleClick ? () => handleClick(row?.original) : undefined
                  }
                  {...(rowClassName ? { className: rowClassName } : {})}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      className={classNames(
                        tableCellClassName,
                        tableView === TableView.TwoRows
                          ? 'h-[56px]'
                          : 'h-[44px]'
                      )}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {paginationConfig.isPaginated ? (
          <div className="flex items-center justify-between rounded-md p-4 bg-bg-secondary">
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={paginationConfig.onPreviousPage}
                disabled={!paginationConfig.hasPreviousPage}
                className="flex items-center gap-1 text-text-primary"
                size="sm"
              >
                <Icons.ArrowNarrowLeft className="w-4 h-4" />
                <span className="txt-small-bold">Previous</span>
              </Button>
              <Button
                variant="secondary"
                onClick={paginationConfig.onNextPage}
                disabled={!paginationConfig.hasNextPage}
                className="flex items-center gap-1 text-text-primary"
                size="sm"
              >
                <span className="txt-small-bold">Next</span>
                <Icons.ArrowNarrowRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <p className="txt-small-medium text-text-quaternary shrink-0">
                {paginationConfig?.placeholder}
              </p>
              <Select
                onValueChange={(val) => {
                  paginationConfig.onPageSizeChange?.(+val);
                }}
                value={paginationConfig.currentPageSize?.toString() || '10'} // Add fallback
                defaultValue="10"
              >
                <SelectTrigger className="min-w-[80px] h-[38px]">
                  <SelectValue>
                    {paginationConfig.currentPageSize ||
                      paginationConfig.pageRowCount}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {['10', '25', '50', '100'].map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}
      </div>

      {selectedRows && selectedRows?.length > 0 && (
        <FloatingActionBar
          selectedCount={selectedRows.length}
          renderChildren={floatingActionItems}
          onClose={() => {}}
        />
      )}
    </div>
  );
}
